import { Button } from '@/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/dropdown';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/form/select';
import { Modal } from '@/components/modal';
import { DataTable } from '@/components/table/datatable';
import { clearErrors } from '@/reducers/profile';
import { AppDispatch } from '@/root/store';
import {
  GetOrgPaymentProviders,
  GetPaymentProviders,
  StorePaymentProviders,
  UpdatePaymentProviders,
} from '@/services/profile';
import { NoSymbolIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import {
  CreditCardIcon,
  DocumentCheckIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/solid';
import { Select } from '@radix-ui/react-select';
import { ColumnDef } from '@tanstack/react-table';
import { InputSwitch } from 'primereact/inputswitch';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

interface Transaction {
  id: string;
  provider_name: string;
  status: boolean;
  channels: [];
}

export function OrgSettingCommercePayment() {
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [selectedOrgPaymentProvider, setSelectedOrgPaymentProvider] = useState({
    id: '',
  });

  const [selectedPaymentProvider, setSelectedPaymentProvider] = useState({
    id: '',
    channels: [],
  });
  const [formData, setFormData] = useState({
    payment_channel_ids: [],
    payment_provider_id: '',
  });

  const [modal, setModal] = useState({
    status: false,
    mode: 'create',
  });

  const {
    orgPaymentProviders,
    paymentProviders,
    updatedState,
    paginator,
    errors,
  } = useSelector((state: any) => state.profile);

  const setData = (value: string, element: string) => {
    setFormData({ ...formData, [element]: value });

    dispatch(clearErrors());
  };

  const submit = () => {
    if (modal.mode === 'create') {
      dispatch(StorePaymentProviders(formData));
    } else {
      dispatch(UpdatePaymentProviders(selectedOrgPaymentProvider.id, formData));
    }
  };

  const toggleTicket = (id: string, status: boolean) => {
    dispatch(
      UpdatePaymentProviders(id, {
        status: status,
      })
    );
  };

  const queryParams = new URLSearchParams(location.search);
  const searchQuery: string = queryParams.get('search') ?? '';

  useEffect(() => {
    dispatch(GetOrgPaymentProviders(searchQuery, page));
    dispatch(GetPaymentProviders(searchQuery, page));
    setModal({ ...modal, status: false });
  }, [searchQuery, page, updatedState]);

  const columns: ColumnDef<Transaction>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        return (
          <>
            <div>
              {row.original.provider_name} {row.original.status}
            </div>
          </>
        );
      },
    },
    {
      accessorKey: 'channels',
      header: 'Metode Pembayaran',
      cell: ({ row }) => {
        return (
          <>
            <div>
              {row.original.channels
                ?.map((channel: any) => channel.name)
                .join(', ')}
            </div>
          </>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        return (
          <>
            <InputSwitch
              pt={{
                slider: {
                  className: row.original.status ? 'bg-primary' : '',
                },
              }}
              checked={row.original.status}
              onChange={() =>
                toggleTicket(row.original.id, !row.original.status)
              }
            />
          </>
        );
      },
    },
    {
      header: 'Aksi',
      cell: ({ row }) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button
                size='sm'
                variant='neutral'
                leftIcon={<EllipsisHorizontalIcon />}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  setSelectedPaymentProvider(row.original);
                  setSelectedOrgPaymentProvider({
                    id: row.original.id,
                  });
                  setModal({ ...modal, status: true, mode: 'edit' });
                }}
              >
                <PencilSquareIcon className='mr-2 h-4 w-4' />
                <span>Ubah</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        show={modal.status}
        onRequestClose={() => setModal({ ...modal, status: false })}
        width='medium'
        title={`${modal.mode === 'create' ? 'Tambah' : 'Edit'} Penyedia Pembayaran`}
        body={
          <div className='flex flex-col gap-4'>
            {modal.mode === 'create' && (
              <FormLabel
                label='Payment Gateway'
                errorText={errors.payment_provider_id}
                required
              >
                <Select
                  onValueChange={(e) => {
                    setData(e, 'payment_provider_id');
                    let paymentProvider = paymentProviders.filter(
                      (provider: any) => provider.id === e
                    )[0];
                    setSelectedPaymentProvider(paymentProvider);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue defaultValue='0' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {paymentProviders.map((provider: any) => (
                        <SelectItem value={provider.id}>
                          {provider.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </FormLabel>
            )}
            {selectedPaymentProvider?.channels?.length > 0 && (
              <FormLabel label='Metode Pembayaran' required>
                <p className={'text-xs'}>
                  {selectedPaymentProvider.channels
                    ?.map((c: any) => c.name)
                    .join(', ')}
                </p>
              </FormLabel>
            )}
            <FormLabel label='Client Id' errorText={errors.client_id} required>
              <Input
                type='text'
                onChange={(e) => setData(e.target.value, 'client_id')}
                required
              />
            </FormLabel>
            <FormLabel
              label='Secret Key'
              errorText={errors.secret_key}
              required
            >
              <Input
                type='password'
                onChange={(e) => setData(e.target.value, 'secret_key')}
                required
              />
            </FormLabel>
            <FormLabel label='Callback Token' errorText={errors.callback_token}>
              <Input
                type='password'
                onChange={(e) => setData(e.target.value, 'callback_token')}
              />
            </FormLabel>
          </div>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setModal({ ...modal, status: false })}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button onClick={submit} leftIcon={<DocumentCheckIcon />}>
              Simpan
            </Button>
          </div>
        }
      />
      <DataTable
        ctaButton={
          <div className={'flex gap-2'}>
            <Button
              variant='primary'
              leftIcon={<CreditCardIcon />}
              onClick={() => {
                setModal({
                  status: true,
                  mode: 'create',
                });
              }}
            >
              Tambah pembayaran
            </Button>
          </div>
        }
        name='Daftar Penyedia pembayaran'
        noun='penyedia pembayaran'
        columns={columns}
        data={orgPaymentProviders}
        total={paginator.to}
        currentTotal={paginator.total}
        currentPage={paginator.current_page}
        pages={paginator.last_page}
        onPageRequest={(nextPage) => setPage(nextPage)}
      />
    </>
  );
}
